import { FormGroup } from '@angular/forms';
import { QuestionAnswerRequest } from '@shared/models/question-answer-request';
import { Calendar } from '@shared/models/question-types/calendar';
import { FileUpload } from '@shared/models/question-types/file-upload';
import { Hierarchical } from '@shared/models/question-types/hierarchical';
import { Matrix } from '@shared/models/question-types/matrix';
import { MultiSelect } from '@shared/models/question-types/multi-select';
import {
  PriorityDragDrop,
  PriorityDragDropItem,
} from '@shared/models/question-types/priority-dragdrop';
import { PriorityDropdown } from '@shared/models/question-types/priority-dropdown';
import { SingleSelect } from '@shared/models/question-types/single-select';
import { Question } from '@shared/models/views-models/question-view';
import { MultiMatrix } from '../models/question-types/multi-matrix';

export const getQuestionAnswers = (
  form: FormGroup,
  questions: Question[],
  responseId: string = '',
): QuestionAnswerRequest[] => {
  return questions
    .flatMap((question) => handleQuestionAnswer(form, question, responseId))
    .filter(Boolean) as QuestionAnswerRequest[];
};

export const handleQuestionAnswer = (
  form: FormGroup,
  question: Question,
  responseId: string,
): QuestionAnswerRequest[] => {
  switch (question.type) {
    case 'OPENANSWER':
      return [createOpenAnswer(form, question, responseId)];
    case 'MATRIX':
      return createMatrixAnswer(form, question, responseId);
    case 'MULTI_MATRIX':
      return createMultiMatrixAnswer(form, question, responseId);
    case 'SINGLE_CHOICE':
      return [createSingleChoiceAnswer(form, question, responseId)].filter(
        Boolean,
      ) as QuestionAnswerRequest[];
    case 'MULTIPLE_CHOICE':
      return [createMultipleChoiceAnswer(form, question, responseId)].filter(
        Boolean,
      ) as QuestionAnswerRequest[];
    case 'HIERARCHICAL':
      return createHierarchicalAnswer(
        form.get(question.id) as FormGroup,
        question,
        responseId,
      );
    case 'INFORMATION':
      return [];
    case 'PRIORITY_DROPDOWN':
      return createPriorityDropdownAnswer(form, question, responseId);
    case 'CALENDAR':
      return [createCalendarAnswer(form, question, responseId)];
    case 'FILE_UPLOAD':
      return [createFileUploadAnswer(form, question, responseId)];
    case 'PRIORITY_DRAGDROP':
      return createPriorityDragDropAnswer(form, question, responseId);
    default:
      console.error('Unknown question type');
      return [];
  }
};

const createOpenAnswer = (
  form: FormGroup,
  question: Question,
  responseId: string,
): QuestionAnswerRequest => ({
  type: 'TEXT_ANSWER',
  data: {
    $type: 'openAnswerResponseData',
    questionId: question.id,
    answer: form.get(question.id)?.value || '',
  },
  responseId,
});

const createMultiMatrixAnswer = (
  form: FormGroup,
  question: MultiMatrix,
  responseId: string,
): QuestionAnswerRequest[] => {
  if (!Array.isArray(question.columns) || question.columns.length === 0) {
    // Edge case: Previewing a draft Quest created from scratch with default MultiMatrix question (no columns, no rows).
    return [];
  }

  const col1: QuestionAnswerRequest[] = question.columns[0].rows.flatMap(
    (row) => {
      switch (row.type) {
        case 'OPENANSWER':
          return [
            {
              type: 'TEXT_ANSWER',
              data: {
                $type: 'openAnswerResponseData',
                questionId: row.id,
                answer: form.get(row.id)?.value || '',
              },
              responseId,
            },
          ];
        case 'SINGLE_CHOICE':
          return createSingleChoiceAnswer(form, row, responseId)
            ? [createSingleChoiceAnswer(form, row, responseId)!]
            : [];
        case 'MULTIPLE_CHOICE':
          return createMultipleChoiceAnswer(form, row, responseId)
            ? [createMultipleChoiceAnswer(form, row, responseId)!]
            : [];
        default:
          return [];
      }
    },
  );
  const col2: QuestionAnswerRequest[] = question.columns[1].rows.flatMap(
    (row) => {
      switch (row.type) {
        case 'OPENANSWER':
          return [
            {
              type: 'TEXT_ANSWER',
              data: {
                $type: 'openAnswerResponseData',
                questionId: row.id,
                answer: form.get(row.id)?.value || '',
              },
              responseId,
            },
          ];
        case 'SINGLE_CHOICE':
          return createSingleChoiceAnswer(form, row, responseId)
            ? [createSingleChoiceAnswer(form, row, responseId)!]
            : [];
        case 'MULTIPLE_CHOICE':
          return createMultipleChoiceAnswer(form, row, responseId)
            ? [createMultipleChoiceAnswer(form, row, responseId)!]
            : [];
        default:
          return [];
      }
    },
  );

  return [...col1, ...col2];
};

const createMatrixAnswer = (
  form: FormGroup,
  question: Matrix,
  responseId: string,
): QuestionAnswerRequest[] => {
  return question.rows.flatMap((row) => {
    switch (row.type) {
      case 'OPENANSWER':
        return [
          {
            type: 'TEXT_ANSWER',
            data: {
              $type: 'openAnswerResponseData',
              questionId: row.id,
              answer: form.get(row.id)?.value || '',
            },
            responseId,
          },
        ];
      case 'SINGLE_CHOICE':
        return createSingleChoiceAnswer(form, row, responseId)
          ? [createSingleChoiceAnswer(form, row, responseId)!]
          : [];
      case 'MULTIPLE_CHOICE':
        return createMultipleChoiceAnswer(form, row, responseId)
          ? [createMultipleChoiceAnswer(form, row, responseId)!]
          : [];
      default:
        return [];
    }
  });
};

const createSingleChoiceAnswer = (
  form: FormGroup,
  question: SingleSelect,
  responseId: string,
): QuestionAnswerRequest | null => {
  const selectedValue = form.get(question.id)?.value;
  if (!selectedValue && selectedValue !== 0)
    return createResetAnswer(question.id, responseId);

  const isOtherSelected = question.alternatives.some(
    (alt) => alt.type === 'OTHER' && alt.id === selectedValue,
  );

  const alternativeId = isOtherSelected
    ? question.alternatives.find((alt) => alt.type === 'OTHER')?.id
    : question.alternatives.find((alt) => {
        // NOTE for presentation: regular slider = 'SLIDER', NPS slider = 'NPS,SLIDER'
        if (question.presentation.endsWith('SLIDER')) {
          return alt.orderNumber?.toString() === selectedValue.toString();
        } else {
          return alt.id === selectedValue;
        }
      })?.id;
  const data: any = {
    $type: 'singleSelectResponseData',
    questionId: question.id,
    alternative: alternativeId,
  };

  if (isOtherSelected) data.otherText = form.get(`${question.id}-OTHER`)?.value;

  return {
    type: 'SINGLE_SELECT',
    data,
    responseId,
  };
};

const createMultipleChoiceAnswer = (
  form: FormGroup,
  question: MultiSelect,
  responseId: string,
): QuestionAnswerRequest | null => {
  const questionFormGroup = form.get(question.id) as FormGroup;
  if (!questionFormGroup) return null;

  const selectedAlternatives: string[] = question.alternatives
    .filter((alternative) => questionFormGroup.get(alternative.id)?.value)
    .map((alternative) => alternative.id);

  if (selectedAlternatives.length === 0) {
    return createResetAnswer(question.id, responseId);
  }

  const isOtherSelected = question.alternatives.some(
    (alt) => alt.type === 'OTHER' && selectedAlternatives.includes(alt.id),
  );
  const otherText = isOtherSelected
    ? form.get(`${question.id}-OTHER`)?.value || ''
    : undefined;

  return {
    type: 'MULTI_SELECT',
    data: {
      $type: 'multiSelectResponseData',
      questionId: question.id,
      alternatives: selectedAlternatives,
      ...(isOtherSelected ? { otherText } : {}),
    },
    responseId,
  };
};

function createHierarchicalAnswer(
  form: FormGroup<any>,
  question: Hierarchical,
  responseId: string,
): QuestionAnswerRequest[] {
  const enabledLevels = question.levels.filter(
    (level) => form.get(`${question.id}_${level.id}`)?.enabled === true,
  );
  return enabledLevels.map((level) => {
    const control = form.get(`${question.id}_${level.id}`);
    if (control?.value) {
      return {
        type: 'HIERARCHICAL',
        data: {
          $type: 'hierarchicalResponseData',
          questionId: level.id,
          answer: control?.value.id || '',
        },
        responseId,
      };
    } else {
      return createResetAnswer(level.id, responseId);
    }
  });
}

function createPriorityDropdownAnswer(
  form: FormGroup,
  question: PriorityDropdown,
  responseId: string,
): QuestionAnswerRequest[] {
  const priorityFormGroup = form.get(question.id) as FormGroup;

  return Object.keys(priorityFormGroup.controls).map((controlName) => {
    const control = priorityFormGroup.controls[controlName];
    const questionId = controlName;
    const answer = control.value;

    if (answer) {
      return {
        type: 'PRIORITY_DROPDOWN',
        data: {
          $type: 'priorityResponseDropData',
          questionId,
          answer: answer,
        },
        responseId,
      };
    } else {
      return createResetAnswer(questionId, responseId);
    }
  });
}

function createCalendarAnswer(
  form: FormGroup,
  question: Calendar,
  responseId: string,
): QuestionAnswerRequest {
  const control = form.get(question.id);

  if (control?.value) {
    const isoDate = new Date(control.value).toISOString().substring(0, 10);
    return {
      type: 'CALENDAR',
      data: {
        $type: 'calendarResponseData',
        questionId: question.id,
        isoDate,
      },
    };
  } else {
    return createResetAnswer(question.id, responseId);
  }
}

function createFileUploadAnswer(
  form: FormGroup,
  question: FileUpload,
  responseId: string,
): QuestionAnswerRequest {
  const control = form.get(question.id);

  if (control?.value) {
    return {
      type: 'FILE_UPLOAD',
      data: {
        $type: 'fileUploadResponseData',
        questionId: question.id,
        fileName: control.value,
        uploadId: question.uploadId,
      },
      responseId,
    };
  } else {
    return createResetAnswer(question.id, responseId);
  }
}

function createPriorityDragDropAnswer(
  form: FormGroup,
  question: PriorityDragDrop,
  responseId: string,
): QuestionAnswerRequest[] {
  const selectedItems = form.get(question.id)?.value;
  return [
    {
      type: 'PRIORITY_DRAG_DROP',
      data: {
        $type: 'priorityDragDropResponseData',
        questionId: question.id,
        answers: selectedItems.map((item: PriorityDragDropItem) => item.id),
      },
      responseId,
    },
  ];
}

function createResetAnswer(
  questionId: string,
  responseId: string,
): QuestionAnswerRequest {
  return {
    type: 'RESET',
    data: {
      $type: 'resetResponseData',
      questionId,
    },
    responseId,
  };
}
